/**
 * PhotoViewer — listing photo gallery for the PDP, ported from od-prototypes photo-viewer v2.
 *
 * Layout
 * - Fills its container's width and sets its own height. An 8-column grid spans the
 *   full width, padding included. The hero (photo 0) starts after the 16px padding and
 *   ends on column line 4; it is a true 4:3 and its height sets the component height.
 * - Later columns are sized from the stage height: wide photos get a full-height column
 *   (aspect 1.5), others stack two per column (aspect 1.32), with a 4px gap.
 *
 * Behaviour
 * - The stage never scrolls natively. It moves via the thumbnail scrubber (drag, or tap a
 *   thumb), a horizontal trackpad swipe over the photos, or ← / →, always settling on a
 *   column boundary.
 * - Counter (top-left) and a share / heart pill (top-right) sit 12px inside the photo area.
 * - Zoom: click a photo (or pinch out) and it expands to fill the window; every photo is
 *   then stacked vertically, scroll-snapped one at a time. Click, pinch in, or Esc closes.
 *   The zoom layer is portalled to <body>.
 * - prefers-reduced-motion: every animation becomes a cut.
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import type { ReactNode } from 'react';

import { IconHeart, IconShare2, IconX } from '@tabler/icons-react';
import { formatStreetAddress } from 'components/marketplace/helpers/addressHelpers';
import { buildGalleryPhotos } from 'components/marketplace/helpers/galleryPhotos';
import type { GalleryPhoto } from 'components/marketplace/helpers/galleryPhotos';
import {
  buildWorkerPhotoUrl,
  getLightboxThumbnailUrl,
  pickFirstPresizedUrl,
} from 'components/marketplace/helpers/presizedPhotos';
import { PropertyPhotoPlaceholder } from 'components/marketplace/shared/PropertyPhoto';
import {
  streetViewAltText,
  streetViewLocationFor,
  StreetViewPhoto,
} from 'components/marketplace/shared/StreetViewPhoto';
import { useTouring } from 'components/self-tour/hooks/useTouring';
import { animate } from 'framer-motion';
import { toImagesWorkerSourceUrl } from 'helpers/imageUrl';
import { createPortal } from 'react-dom';

import { BuyDirectContextModal } from '../PropertySection/BuyDirectContextModal';
import { SelfTourDialog } from '../PropertySection/SelfTourDialog';
import { TourWithAgentModal } from '../PropertySection/TourWithAgentModal';
import type { PropertyDetailsType } from '../types';

export interface ViewerPhoto {
  /** xl URL for the zoom morph and stack. The grid uses `srcSet` of the same 3:2 crop. */
  src: string;
  /** Responsive candidates for the grid and the server-rendered hero. */
  srcSet?: string;
  /** Small render for the scrubber. */
  thumb: string;
  alt: string;
  /** true → full-height column; false → stacked two per column. */
  wide: boolean;
}

/* ---- locked design values ---------------------------------------------------- */
const CONFIG = {
  grid: {
    padding: 0,
    gap: 4,
    heroRatio: 4 / 3,
    topRadius: 14,
    heroSpan: 4,
    columns: 8,
  },
  columnsAfter: {
    stackedAspect: 1.32,
    wideAspect: 1.5,
  },
  zoom: {
    open: { duration: 0.45, ease: [0.65, 0, 0.35, 1] as [number, number, number, number] },
    close: { duration: 0.35, ease: [0.65, 0, 0.35, 1] as [number, number, number, number] },
    sideInset: 80,
    stackGap: 4,
    snap: true,
    backdrop: '#000',
  },
};
type ViewerConfig = typeof CONFIG;
const INK = '#231E1B';
const CANVAS = '#F9F8F5';
/** Height of the site header the zoom bar replaces while a photo is open. */
const BAR_H = 72;
/** Zoomed photos come from the 3:2 image preset. */
const ZOOM_PHOTO_RATIO = 3 / 2;

/** Below this width the viewer behaves like a phone: one photo at a time, edge-to-edge scrubber. */
const PHONE_MAX_WIDTH = 768;
const isPhone = (W: number) => W < PHONE_MAX_WIDTH;

function geometry(W: number, g: ViewerConfig['grid']) {
  const colW = W / g.columns;
  // On phones the first photo fills the whole frame; the rest are reached via the scrubber.
  const heroSpan = isPhone(W) ? g.columns : g.heroSpan;
  const heroW = heroSpan * colW - g.padding;
  const heroH = Math.round(heroW / g.heroRatio);
  return { colW, heroW, heroH, frameH: heroH + 2 * g.padding };
}

const CSS = `
  .pv-root{
    --bg: #ffffff;
    --gap: 4px;
    --pill-h: 46px;
    --od-canvas: ${CANVAS};
    position:relative; width:100%;
    padding:var(--pad, 16px);
    background:var(--bg);
    overflow:hidden;
    -webkit-font-smoothing:antialiased;
    /* Before the engine measures, hold the frame the hero will get: 4/8 of the width at 4:3. */
    aspect-ratio: 8 / 3;
  }
  /* The server-rendered hero: exactly where the hero cell goes, so the grid replaces it seamlessly. */
  .pv-root .ssr-hero{
    position:absolute; left:0; top:0; height:100%; width:50%;
    object-fit:cover; display:block; border-radius:var(--stage-radius, 14px) 0 0 var(--stage-radius, 14px);
  }
  .pv-root .ssr-hero[hidden]{ display:none; }
  .pv-root.empty .empty-hero{ position:absolute; inset:0; }
  .pv-root.empty .empty-hero > *{ height:100%; width:100%; }
  .pv-root.empty .scrubber,
  .pv-root.empty .counter,
  .pv-root.empty .actions{ display:none; }
  @media (max-width: ${PHONE_MAX_WIDTH - 1}px){
    .pv-root{ aspect-ratio: 4 / 3; }
    .pv-root .ssr-hero{ width:100%; border-radius:var(--stage-radius, 14px); }
  }
  .pv-root, .pv-root *{ box-sizing:border-box; }
  .pv-root .viewer{ position:absolute; inset:var(--pad, 16px); display:flex; flex-direction:column; }
  .pv-root .stage{
    flex:1 1 auto; position:relative; overflow:hidden; touch-action:pan-y;
    border-radius:var(--stage-radius, 14px);
  }
  .pv-root .track{ display:flex; gap:var(--gap); height:100%; align-items:stretch; width:max-content; }
  .pv-root .col{ flex:0 0 auto; height:100%; display:flex; flex-direction:column; gap:var(--gap); }
  .pv-root .cell{ position:relative; flex:1 1 0; overflow:hidden; background:#e9e6e2; cursor:pointer; }
  .pv-root .cell img{ width:100%; height:100%; object-fit:cover; display:block; user-select:none; -webkit-user-drag:none; }
  .pv-root .hero-overlay{ position:absolute; z-index:25; pointer-events:none; }
  .pv-root .hero-overlay > *{ pointer-events:auto; }

  .pv-zoom{ position:fixed; inset:0; z-index:3000; display:none; --od-canvas: ${CANVAS}; -webkit-font-smoothing:antialiased; }
  .pv-zoom.on{ display:block; }
  /* Drop below body-portaled dialogs while share, tour, or buy is open. */
  .pv-zoom.yield{ z-index:0; }
  .pv-zoom *{ box-sizing:border-box; }
  .pv-zoom .zoom-backdrop{ position:absolute; inset:0; opacity:0; }
  .pv-zoom .zoom-img{ position:absolute; left:0; top:0; overflow:hidden; cursor:zoom-out; }
  .pv-zoom .zoom-img img{ width:100%; height:100%; object-fit:cover; display:block; -webkit-user-drag:none; }
  .pv-zoom .zoom-scroll{
    position:absolute; left:0; top:0; width:100%; overflow-y:auto; overflow-x:hidden;
    overscroll-behavior:contain;
    display:flex; flex-direction:column; gap:var(--zoom-gap, 4px);
    opacity:0; pointer-events:none;
  }
  .pv-zoom .zoom-scroll.snap{ scroll-snap-type:y mandatory; }
  .pv-zoom .zoom-scroll.live{ opacity:1; pointer-events:auto; }
  .pv-zoom .zoom-slot{
    flex:none; width:100%; height:var(--slot-h, 100%); display:flex; align-items:center; justify-content:center;
    scroll-snap-align:start; overflow:hidden; cursor:zoom-out;
  }
  .pv-zoom .zoom-frame{
    width:var(--photo-w, 100%); height:var(--photo-h, 100%); overflow:hidden; cursor:zoom-in;
  }
  .pv-zoom .zoom-frame img{
    width:100%; height:100%; object-fit:contain; display:block; -webkit-user-drag:none;
    transform-origin:0 0; transition:transform .08s linear;
  }
  .pv-zoom .zoom-frame.toggling img{ transition:transform .25s ease; }
  .pv-zoom .zoom-frame.magnified{ cursor:zoom-out; touch-action:none; }
  .pv-zoom .zoom-frame.panning img{ transition:none; }

  .pv-root .actions, .pv-zoom .actions{
    position:absolute; top:12px; right:12px; z-index:30;
    display:flex; align-items:center; gap:20px;
    padding:8px; border-radius:999px;
    background:var(--od-canvas);
  }
  .pv-root .icon-btn, .pv-zoom .icon-btn{
    width:24px; height:24px; padding:0; border:none; background:none;
    display:grid; place-items:center; cursor:pointer; color:${INK};
  }
  .pv-root .icon-btn svg, .pv-zoom .icon-btn svg{ width:24px; height:24px; display:block; }
  .pv-root .icon-btn.liked, .pv-zoom .icon-btn.liked{ color:#e8318a; }
  .pv-root .icon-btn.liked svg path, .pv-zoom .icon-btn.liked svg path{ fill:currentColor; }
  .pv-root .counter, .pv-zoom .counter{
    position:absolute; top:12px; left:12px; z-index:30;
    height:40px; display:flex; align-items:center;
    background:var(--od-canvas); color:${INK}; font-size:12.5px; letter-spacing:.02em;
    padding:0 11px; border-radius:999px; font-variant-numeric:tabular-nums;
  }

  .pv-root .scrubber{
    position:absolute; left:0; right:0; bottom:26px; z-index:20;
    display:flex; align-items:center; justify-content:center; padding:0 24px; pointer-events:none;
  }
  .pv-root .strip-wrap{ position:relative; pointer-events:auto; max-width:100%; }
  .pv-root .pill{
    position:relative; height:var(--pill-h); border-radius:10px;
    background:rgba(40,34,30,.72); backdrop-filter:blur(8px); -webkit-backdrop-filter:blur(8px);
    cursor:grab; touch-action:none;
  }
  .pv-root .pill.scrubbing{ cursor:grabbing; }
  .pv-root .pill::after{ content:''; position:absolute; left:-8px; right:-8px; top:-12px; bottom:-12px; }
  .pv-root .window{
    position:absolute; background:#fff; border-radius:10px;
    box-shadow:0 2px 10px rgba(0,0,0,.22); z-index:1; pointer-events:none;
  }
  .pv-root .clip{
    position:absolute; inset:0; border-radius:inherit; overflow:hidden;
    -webkit-mask-image:linear-gradient(to right, transparent 0, #000 var(--fadeL, 0px), #000 calc(100% - var(--fadeR, 0px)), transparent 100%);
    mask-image:linear-gradient(to right, transparent 0, #000 var(--fadeL, 0px), #000 calc(100% - var(--fadeR, 0px)), transparent 100%);
  }
  .pv-root .rail{ position:absolute; top:0; left:0; height:100%; will-change:transform; }
  .pv-root .thumbs{ position:absolute; inset:0; z-index:2; }
  .pv-root .thumb{ position:absolute; top:50%; left:0; border-radius:4px; overflow:hidden; background:#3a3330; transform-origin:left center; }
  .pv-root .thumb img{ width:100%; height:100%; object-fit:cover; display:block; -webkit-user-drag:none; }
  .pv-root .thumb:hover{ filter:brightness(1.18); }
  .pv-zoom .counter{ top:${BAR_H + 12}px; left:calc(var(--zoom-x, 80px) + 12px); opacity:0; }

  /* The bar that stands in for the site header while zoomed: same 72px height, new content. */
  .pv-zoom .zoom-bar{
    position:absolute; top:0; left:0; right:0; height:${BAR_H}px; z-index:40; opacity:0;
    display:flex; align-items:center; justify-content:space-between;
    padding:0 80px; background:#fff; border-bottom:1px solid #EAE8E0;
  }
  .pv-zoom .zoom-bar-address{ display:flex; align-items:baseline; gap:8px; min-width:0; }
  .pv-zoom .zoom-bar-right{ display:flex; align-items:center; }
  .pv-zoom .zoom-bar-icons{ display:flex; align-items:center; gap:20px; margin-left:36px; }
  .pv-zoom .zoom-bar-address > span{ white-space:nowrap; }
  .pv-zoom .zoom-bar-address > span:first-child{ overflow:hidden; text-overflow:ellipsis; }
  .pv-root .stage.swiping .cell{ cursor:grabbing; }
  /* A lone photo (a floor plan) is shown whole, with no counter and no stack. */
  .pv-zoom.single .counter{ display:none; }
  .pv-zoom.single .zoom-img img{ object-fit:contain; }
  @media (max-width: ${PHONE_MAX_WIDTH - 1}px){
    .pv-zoom .zoom-bar{ padding:0 16px; }
    .pv-zoom .zoom-bar-address{ flex-direction:column; align-items:flex-start; gap:0; }
    .pv-zoom .zoom-bar-actions{ display:none; }
    .pv-zoom .zoom-bar-icons{ gap:16px; margin-left:16px; }
  }
`;

/* PAD is every gap in the scrubber; a boundary between inside and outside measures PAD + PAD. */
const PAD = 8;
const THUMB_ASPECT = 4 / 3;
const COVER_IN = 0.05;
const COVER_FULL = 0.8;
const COVER_MID = (COVER_IN + COVER_FULL) / 2;
const COVER_HYST = 0.05;
const SETTLE_MS = 160;
const LIVE_IN_MS = 130;
const SIDE_ROOM = 22 + 38 * 2 + 10 + 28;
/** Matches the scrubber's own side padding, so the pill spans the phone frame edge to edge. */
const SCRUBBER_EDGE = 24;

function fallback(seed: string, w: number, h: number) {
  let n = 0;
  for (const c of seed) n = (n * 31 + c.charCodeAt(0)) % 360;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
    <defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stop-color="hsl(${n},32%,72%)"/>
      <stop offset="1" stop-color="hsl(${(n + 40) % 360},28%,48%)"/>
    </linearGradient></defs>
    <rect width="100%" height="100%" fill="url(#g)"/></svg>`;
  return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
}
function makeImg(src: string, alt: string, w: number, h: number, srcSet?: string, sizes?: string) {
  const img = document.createElement('img');
  img.src = src;
  if (srcSet) {
    img.srcset = srcSet;
    if (sizes) img.sizes = sizes;
  }
  img.alt = alt;
  img.draggable = false;
  img.addEventListener(
    'error',
    () => {
      img.removeAttribute('srcset');
      img.src = fallback(alt, w, h);
    },
    { once: true },
  );
  return img;
}
function smoothstep(e0: number, e1: number, x: number) {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
}
function el(scope: ParentNode, key: string): HTMLElement {
  const node = scope.querySelector(`[data-pv="${key}"]`);
  if (!(node instanceof HTMLElement)) throw new Error(`PhotoViewer: missing [data-pv="${key}"]`);
  return node;
}

type Range = [number, number];
type Rect = { x: number; y: number; w: number; h: number };
type Callbacks = { onShare?: () => void; onLike?: (liked: boolean) => void };
type DragState = {
  id: number;
  startX: number;
  startScroll: number;
  gain: number;
  slop: number;
  lastX: number;
  lastT: number;
  v: number;
  thumbIdx: number;
  moved: boolean;
};

/* All viewer behaviour. Runs once per mounted root; returns a teardown. */
const VIEWER_IMAGE_SIZES = '(max-width: 767px) 100vw, 50vw';

function isTextEntry(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  return target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT';
}

function mountViewer(
  root: HTMLElement,
  zoom: HTMLElement,
  PHOTOS: ViewerPhoto[],
  cfg: ViewerConfig,
  callbacks: Callbacks,
) {
  const viewer = el(root, 'viewer');
  const stage = el(root, 'stage');
  const track = el(root, 'track');
  const pill = el(root, 'pill');
  const clip = el(root, 'clip');
  const rail = el(root, 'rail');
  const thumbsEl = el(root, 'thumbs');
  const winEl = el(root, 'window');
  const counter = el(root, 'counter');
  const likeBtn = el(root, 'like');
  const shareBtn = el(root, 'share');
  const heroOverlay = root.querySelector('[data-pv="heroOverlay"]');
  const inert = !!root.closest('[inert]');

  const cells: { colIndex: number; el: HTMLElement }[] = [];
  const columns: { el: HTMLElement; photos: number[] }[] = [];
  const thumbEls: HTMLElement[] = [];
  let tW = 40;
  let tH = 30;
  let maxActive = 1;
  let colGeom: { l: number; w: number }[] = [];
  let colIn: boolean[] = [];

  function buildStage() {
    track.innerHTML = '';
    cells.length = 0;
    columns.length = 0;
    let i = 0;
    while (i < PHOTOS.length) {
      const col = document.createElement('div');
      col.className = 'col';
      const group: number[] = [];
      if (PHOTOS[i].wide || i === PHOTOS.length - 1) {
        group.push(i);
        i++;
      } else {
        group.push(i);
        i++;
        if (i < PHOTOS.length && !PHOTOS[i].wide) {
          group.push(i);
          i++;
        }
      }
      group.forEach((pi) => {
        const cell = document.createElement('div');
        cell.className = 'cell';
        cell.appendChild(
          makeImg(
            PHOTOS[pi].src,
            PHOTOS[pi].alt,
            1600,
            1067,
            PHOTOS[pi].srcSet,
            VIEWER_IMAGE_SIZES,
          ),
        );
        col.appendChild(cell);
        cells[pi] = { colIndex: columns.length, el: cell };
      });
      columns.push({ el: col, photos: group });
      track.appendChild(col);
    }
    sizeColumns();
  }

  function sizeColumns() {
    const H = stage.clientHeight;
    columns.forEach((col, ci) => {
      const n = col.photos.length;
      if (ci === 0 && n === 1) {
        col.el.style.width = geometry(root.clientWidth, cfg.grid).heroW + 'px';
        return;
      }
      const rowH = n === 1 ? H : (H - cfg.grid.gap) / 2;
      const aspect = n === 1 ? cfg.columnsAfter.wideAspect : cfg.columnsAfter.stackedAspect;
      col.el.style.width = Math.round(rowH * aspect) + 'px';
    });
    measureColumns();
  }

  function measureColumns() {
    colGeom = columns.map((c) => ({ l: c.el.offsetLeft, w: c.el.offsetWidth }));
    if (colIn.length !== colGeom.length) colIn = colGeom.map(() => false);
  }

  function buildThumbs() {
    thumbsEl.innerHTML = '';
    thumbEls.length = 0;
    PHOTOS.forEach((p) => {
      const t = document.createElement('div');
      t.className = 'thumb';
      t.appendChild(makeImg(p.thumb || p.src, p.alt, 120, 90));
      thumbsEl.appendChild(t);
      thumbEls.push(t);
    });
    sizeThumbs();
  }

  function trackWidth() {
    const n = PHOTOS.length;
    return n * tW + (n + 3) * PAD;
  }
  let viewW = 0;

  function sizeThumbs() {
    const n = PHOTOS.length;
    if (n === 0) return;
    const sideRoom = isPhone(viewer.clientWidth) ? SCRUBBER_EDGE : SIDE_ROOM;
    const avail = Math.min(viewer.clientWidth - 48 - sideRoom * 2, 1180);
    maxActive = sweepExtremes().maxActive;
    const nRef = Math.min(n, 22);
    const w = (avail - (nRef + 3) * PAD) / nRef;
    tW = Math.max(28, Math.min(Math.round(w / 4) * 4, 44));
    tH = tW / THUMB_ASPECT;
    root.style.setProperty('--pill-h', tH + PAD * 2 + 'px');
    viewW = Math.min(trackWidth(), viewer.clientWidth - sideRoom * 2, 1240);
    pill.style.width = viewW + 'px';
    rail.style.width = trackWidth() + 'px';
  }

  function layoutThumbs(av: number[], disc: number[], range: Range) {
    const n = thumbEls.length;
    let x = PAD * (1 + av[n - 1]);
    const xs = Array.from({ length: n }, () => 0);
    for (let i = 0; i < n; i++) {
      if (i > 0) x += PAD + PAD * Math.abs(av[i] - av[i - 1]);
      const t = thumbEls[i];
      xs[i] = x;
      t.style.width = tW + 'px';
      t.style.height = tH + 'px';
      t.style.transform = 'translate(' + x.toFixed(2) + 'px, -50%)';
      t.style.opacity = (0.72 + 0.28 * av[i]).toFixed(3);
      t.classList.toggle('active', disc[i] === 1);
      x += tW;
    }
    const xAt = (f: number) => {
      f = Math.max(0, Math.min(n - 1, f));
      const i0 = Math.max(0, Math.min(n - 1, Math.floor(f)));
      const t = f - i0;
      const nxt = i0 + 1 < n ? xs[i0 + 1] : xs[i0] + tW + PAD;
      return xs[i0] + t * (nxt - xs[i0]);
    };
    const L = xAt(range[0]) - PAD;
    const R = xAt(range[1]) + tW + PAD;
    winEl.style.left = L.toFixed(2) + 'px';
    winEl.style.width = Math.max(0, R - L).toFixed(2) + 'px';
    winEl.style.height = tH + PAD * 2 + 'px';
    winEl.style.top = '0px';
    const maxOff = Math.max(0, trackWidth() - viewW);
    const off = Math.max(0, Math.min(maxOff, (L + R) / 2 - viewW / 2));
    rail.style.transform = 'translateX(' + (-off).toFixed(2) + 'px)';
    clip.style.setProperty('--fadeL', Math.min(24, off).toFixed(1) + 'px');
    clip.style.setProperty('--fadeR', Math.min(24, maxOff - off).toFixed(1) + 'px');
  }

  function coverAt(s: number) {
    const vw = stage.clientWidth;
    const out = Array.from({ length: colGeom.length }, () => 0);
    for (let i = 0; i < colGeom.length; i++) {
      const g = colGeom[i];
      const ov = Math.max(0, Math.min(s + vw, g.l + g.w) - Math.max(s, g.l));
      out[i] = ov / Math.min(g.w, vw);
    }
    return out;
  }
  function activationsAt(s: number) {
    const cov = coverAt(s);
    const out = Array.from({ length: PHOTOS.length }, () => 0);
    for (let ci = 0; ci < columns.length; ci++) {
      const a = smoothstep(COVER_IN, COVER_FULL, cov[ci]);
      for (const pi of columns[ci].photos) out[pi] = a;
    }
    return out;
  }
  function rangeAt(s: number): Range {
    if (columns.length === 0) return [0, 0];
    const cov = coverAt(s);
    let first = -1;
    let last = -1;
    let best = 0;
    for (let ci = 0; ci < cov.length; ci++) {
      if (cov[ci] > cov[best]) best = ci;
      if (cov[ci] >= COVER_MID) {
        for (const pi of columns[ci].photos) {
          if (first < 0) first = pi;
          last = pi;
        }
      }
    }
    if (first < 0) {
      const p = columns[best].photos;
      return [p[0], p[p.length - 1]];
    }
    return [first, last];
  }
  function visibleRange() {
    return rangeAt(stage.scrollLeft);
  }
  function sweepExtremes() {
    const max = maxScroll();
    let found = 1;
    const stride = Math.max(8, Math.round(max / 400));
    for (let s = 0; s <= max + stride; s += stride) {
      const [a, b] = rangeAt(Math.min(s, max));
      if (b - a + 1 > found) found = b - a + 1;
    }
    return { maxActive: found };
  }

  let liveMode = false;
  let liveT0 = 0;
  let entryAv: number[] | null = null;
  let entryRange: Range | null = null;
  let fromAv: number[] | null = null;
  let fromRange: Range | null = null;
  let rTo: Range | null = null;
  let blend = 1;
  let blendRaf: number | null = null;
  let lastAv: number[] | null = null;
  let lastRange: Range | null = null;
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

  function stopBlend() {
    if (blendRaf) {
      cancelAnimationFrame(blendRaf);
      blendRaf = null;
    }
  }
  const lerpAv = (a: number[], b: number[], t: number) => b.map((v, i) => a[i] + (v - a[i]) * t);
  const lerpRange = (a: Range, b: Range, t: number): Range => [
    a[0] + (b[0] - a[0]) * t,
    a[1] + (b[1] - a[1]) * t,
  ];
  function avOfRange(r: Range) {
    const lo = r[0] - 0.5;
    const hi = r[1] + 0.5;
    const n = PHOTOS.length;
    const out = Array.from({ length: n }, () => 0);
    for (let i = 0; i < n; i++) out[i] = Math.max(0, Math.min(hi, i + 0.5) - Math.max(lo, i - 0.5));
    return out;
  }
  function rangeOfAv(av: number[]): Range {
    let sum = 0;
    let wsum = 0;
    for (let i = 0; i < av.length; i++) {
      sum += av[i];
      wsum += i * av[i];
    }
    if (sum <= 0) return [0, 0];
    const centre = wsum / sum;
    return [centre - (sum - 1) / 2, centre + (sum - 1) / 2];
  }
  function rangeFromColIn(): Range {
    let first = -1;
    let last = -1;
    for (let ci = 0; ci < columns.length; ci++) {
      if (!colIn[ci]) continue;
      for (const pi of columns[ci].photos) {
        if (first < 0) first = pi;
        last = pi;
      }
    }
    return first < 0 ? rangeAt(stage.scrollLeft) : [first, last];
  }
  function beginLive() {
    stopBlend();
    entryAv = lastAv ? lastAv.slice() : activationsAt(stage.scrollLeft);
    entryRange = lastRange ? [lastRange[0], lastRange[1]] : rangeAt(stage.scrollLeft);
    liveMode = true;
    liveT0 = reducedMotion.matches ? 0 : performance.now();
  }
  function endLive(dur = SETTLE_MS) {
    stopBlend();
    fromAv = lastAv ? lastAv.slice() : null;
    fromRange = lastRange ? [lastRange[0], lastRange[1]] : rangeAt(stage.scrollLeft);
    liveMode = false;
    rTo = rangeFromColIn();
    blend = 0;
    if (reducedMotion.matches) {
      blend = 1;
      fromAv = null;
      fromRange = null;
      syncScrubber();
      return;
    }
    const t0 = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / dur);
      blend = 1 - Math.pow(1 - p, 3);
      syncScrubber();
      if (p < 1) blendRaf = requestAnimationFrame(tick);
      else {
        blendRaf = null;
        blend = 1;
        fromAv = null;
        fromRange = null;
        syncScrubber();
      }
    };
    blendRaf = requestAnimationFrame(tick);
  }

  function syncScrubber() {
    if (PHOTOS.length === 0) return;
    const cov = coverAt(stage.scrollLeft);
    const n = PHOTOS.length;
    let av: number[];
    let shown: number[];
    let range: Range;
    if (liveMode) {
      for (let ci = 0; ci < cov.length; ci++) {
        colIn[ci] = cov[ci] >= (colIn[ci] ? COVER_MID - COVER_HYST : COVER_MID + COVER_HYST);
      }
      if (!colIn.some(Boolean)) {
        let best = 0;
        for (let ci = 1; ci < cov.length; ci++) if (cov[ci] > cov[best]) best = ci;
        colIn[best] = true;
      }
      const cont = Array.from({ length: n }, () => 0);
      shown = Array.from({ length: n }, () => 0);
      columns.forEach((c, ci) => {
        const a = smoothstep(COVER_IN, COVER_FULL, cov[ci]);
        const d = colIn[ci] ? 1 : 0;
        for (const pi of c.photos) {
          cont[pi] = a;
          shown[pi] = d;
        }
      });
      const p = Math.min(1, (performance.now() - liveT0) / LIVE_IN_MS);
      const e = 1 - Math.pow(1 - p, 3);
      const contRange = rangeOfAv(cont);
      av = e >= 1 || !entryAv ? cont : lerpAv(entryAv, cont, e);
      range = e >= 1 || !entryRange ? contRange : lerpRange(entryRange, contRange, e);
    } else {
      const to = rTo || rangeAt(stage.scrollLeft);
      const target = avOfRange(to);
      av = !fromAv || blend >= 1 ? target : lerpAv(fromAv, target, blend);
      range = !fromRange || blend >= 1 ? to : lerpRange(fromRange, to, blend);
      shown = Array.from({ length: n }, () => 0);
      for (let i = Math.round(to[0]); i <= Math.round(to[1]); i++) shown[i] = 1;
      for (let ci = 0; ci < columns.length; ci++) colIn[ci] = shown[columns[ci].photos[0]] === 1;
    }
    layoutThumbs(av, shown, range);
    lastAv = av.slice();
    lastRange = [range[0], range[1]];
    let first = -1;
    let last = -1;
    for (let i = 0; i < n; i++)
      if (shown[i]) {
        if (first < 0) first = i;
        last = i;
      }
    counter.textContent =
      first === last ? `${first + 1} of ${n}` : `${first + 1}–${last + 1} of ${n}`;
    pill.setAttribute('aria-valuenow', String(first + 1));
    pill.setAttribute('aria-valuetext', counter.textContent.replace('–', ' to '));
  }

  function maxScroll() {
    return Math.max(0, stage.scrollWidth - stage.clientWidth);
  }
  let anim: { raf: number } | null = null;
  let navTarget: number | null = null;
  function stopAnim() {
    if (anim) {
      cancelAnimationFrame(anim.raf);
      anim = null;
    }
  }
  function goTo(target: number, smooth = true, dur = 380) {
    stopAnim();
    target = Math.max(0, Math.min(target, maxScroll()));
    navTarget = target;
    if (!smooth || reducedMotion.matches) {
      stopBlend();
      liveMode = false;
      stage.scrollLeft = target;
      navTarget = null;
      fromAv = null;
      fromRange = null;
      rTo = rangeAt(target);
      blend = 1;
      syncScrubber();
      return;
    }
    const from = stage.scrollLeft;
    const dist = target - from;
    if (Math.abs(dist) < 1) {
      navTarget = null;
      endLive();
      return;
    }
    stopBlend();
    fromAv = lastAv ? lastAv.slice() : null;
    fromRange = lastRange ? [lastRange[0], lastRange[1]] : rangeAt(from);
    liveMode = false;
    rTo = rangeAt(target);
    blend = 0;
    const t0 = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / dur);
      const e = 1 - Math.pow(1 - p, 3);
      stage.scrollLeft = from + dist * e;
      blend = e;
      syncScrubber();
      if (p < 1 && anim) anim.raf = requestAnimationFrame(tick);
      else {
        anim = null;
        navTarget = null;
        blend = 1;
        fromAv = null;
        fromRange = null;
        syncScrubber();
      }
    };
    anim = { raf: requestAnimationFrame(tick) };
  }
  function scrollToPhoto(idx: number, smooth = true) {
    const cell = cells[idx];
    if (!cell) return;
    goTo(columns[cell.colIndex].el.offsetLeft, smooth);
  }
  function step(dir: number) {
    const cur = navTarget !== null ? navTarget : stage.scrollLeft;
    let target: number;
    if (dir > 0) {
      const nextCol = columns.find((c) => c.el.offsetLeft > cur + 2);
      target = nextCol ? nextCol.el.offsetLeft : maxScroll();
    } else {
      const prevCols = columns.filter((c) => c.el.offsetLeft < cur - 2);
      target = prevCols.length ? prevCols[prevCols.length - 1].el.offsetLeft : 0;
    }
    goTo(target);
  }

  const onKey = (e: KeyboardEvent) => {
    if (zoom.classList.contains('yield')) return;
    if (isTextEntry(e.target)) return;
    const target = e.target;
    const inZoom = target instanceof Node && zoom.contains(target);
    const inViewer = target instanceof Node && root.contains(target);
    const atPage = target === document.body || target === document.documentElement;
    if (zoomed >= 0) {
      if (!inZoom && !atPage) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        closeZoom();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        zoomStep(1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        zoomStep(-1);
      }
      return;
    }
    if (PHOTOS.length === 0 || (!inViewer && !atPage)) return;
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      step(1);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      step(-1);
    }
  };
  if (!inert) window.addEventListener('keydown', onKey);

  let drag: DragState | null = null;
  function scrubGain() {
    const nominalPlate = maxActive * tW + (maxActive + 1) * PAD;
    return maxScroll() / Math.max(1, trackWidth() - nominalPlate);
  }
  const onDown = (e: PointerEvent) => {
    e.preventDefault();
    if (anim) {
      stopAnim();
      navTarget = null;
      endLive();
    }
    const thumb = e.target instanceof Element ? e.target.closest('.thumb') : null;
    drag = {
      id: e.pointerId,
      startX: e.clientX,
      startScroll: stage.scrollLeft,
      gain: scrubGain(),
      slop: e.pointerType === 'touch' ? 10 : 4,
      lastX: e.clientX,
      lastT: performance.now(),
      v: 0,
      thumbIdx: thumb instanceof HTMLElement ? thumbEls.indexOf(thumb) : -1,
      moved: false,
    };
    pill.setPointerCapture(e.pointerId);
  };
  pill.addEventListener('pointerdown', onDown);
  const onMove = (e: PointerEvent) => {
    if (!drag || e.pointerId !== drag.id) return;
    if (!drag.moved) {
      const dx = e.clientX - drag.startX;
      if (Math.abs(dx) < drag.slop) return;
      drag.moved = true;
      stopAnim();
      navTarget = null;
      drag.startX += Math.sign(dx) * drag.slop;
      beginLive();
      pill.classList.add('scrubbing');
      document.body.style.cursor = 'grabbing';
    }
    const now = performance.now();
    const dt = Math.max(1, now - drag.lastT);
    drag.v = 0.75 * drag.v + 0.25 * ((e.clientX - drag.lastX) / dt);
    drag.lastX = e.clientX;
    drag.lastT = now;
    const s = drag.startScroll + (e.clientX - drag.startX) * drag.gain;
    const c = Math.max(0, Math.min(maxScroll(), s));
    if (c !== s) {
      drag.startX = e.clientX;
      drag.startScroll = c;
    }
    stage.scrollLeft = c;
    syncScrubber();
  };
  pill.addEventListener('pointermove', onMove);
  function trackJump(clientX: number) {
    let best = 0;
    let bd = Infinity;
    thumbEls.forEach((t, i) => {
      const r = t.getBoundingClientRect();
      const d = Math.abs(clientX - (r.left + r.width / 2));
      if (d < bd) {
        bd = d;
        best = i;
      }
    });
    scrollToPhoto(best);
  }
  function settleSnap(d: { v: number; gain: number }) {
    const here = stage.scrollLeft;
    const vScroll = (d.v || 0) * d.gain;
    const bounds = columns.map((c) => Math.min(c.el.offsetLeft, maxScroll())).concat([maxScroll()]);
    let bL = 0;
    let bR = maxScroll();
    for (const b of bounds) {
      if (b <= here && b > bL) bL = b;
      if (b >= here && b < bR) bR = b;
    }
    const proj = here + vScroll * 150;
    const target = Math.abs(proj - bL) <= Math.abs(proj - bR) ? bL : bR;
    goTo(target, true, 240);
  }
  function endDrag(e?: PointerEvent) {
    if (!drag || (e && e.pointerId !== drag.id)) return;
    const d = drag;
    drag = null;
    pill.classList.remove('scrubbing');
    document.body.style.cursor = '';
    const isUp = !!e && e.type === 'pointerup';
    if (!d.moved) {
      if (isUp && d.thumbIdx >= 0) scrollToPhoto(d.thumbIdx);
      else if (isUp && e) trackJump(e.clientX);
      else endLive();
    } else {
      settleSnap(d);
    }
  }
  pill.addEventListener('pointerup', endDrag);
  pill.addEventListener('pointercancel', endDrag);

  /* ---------------- stage swipe ---------------- */
  // A finger swipe (or mouse drag) across the photos moves the grid 1:1 and snaps to the nearest
  // column on release. The stage allows vertical pans through, so the page still scrolls.
  type SwipeState = {
    id: number;
    startX: number;
    startScroll: number;
    lastX: number;
    lastT: number;
    v: number;
    slop: number;
    moved: boolean;
  };
  let swipe: SwipeState | null = null;
  let suppressCellClick = false;
  const onStageDown = (e: PointerEvent) => {
    if (zoomed >= 0 || swipe || drag) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (e.target instanceof Element && e.target.closest('.hero-overlay')) return;
    swipe = {
      id: e.pointerId,
      startX: e.clientX,
      startScroll: stage.scrollLeft,
      lastX: e.clientX,
      lastT: performance.now(),
      v: 0,
      slop: e.pointerType === 'touch' ? 10 : 4,
      moved: false,
    };
  };
  const onStageMove = (e: PointerEvent) => {
    if (!swipe || e.pointerId !== swipe.id) return;
    if (!swipe.moved) {
      const dx = e.clientX - swipe.startX;
      if (Math.abs(dx) < swipe.slop) return;
      swipe.moved = true;
      stopAnim();
      navTarget = null;
      beginLive();
      swipe.startX += Math.sign(dx) * swipe.slop;
      stage.setPointerCapture(e.pointerId);
      stage.classList.add('swiping');
    }
    const now = performance.now();
    const dt = Math.max(1, now - swipe.lastT);
    swipe.v = 0.75 * swipe.v + 0.25 * ((e.clientX - swipe.lastX) / dt);
    swipe.lastX = e.clientX;
    swipe.lastT = now;
    const s = swipe.startScroll - (e.clientX - swipe.startX);
    const c = Math.max(0, Math.min(maxScroll(), s));
    if (c !== s) {
      swipe.startX = e.clientX;
      swipe.startScroll = c;
    }
    stage.scrollLeft = c;
    syncScrubber();
  };
  const endSwipe = (e: PointerEvent) => {
    if (!swipe || e.pointerId !== swipe.id) return;
    const s = swipe;
    swipe = null;
    stage.classList.remove('swiping');
    if (!s.moved) return;
    // The click that follows a drag must not open the photo under the pointer.
    suppressCellClick = true;
    setTimeout(() => {
      suppressCellClick = false;
    }, 0);
    // Finger velocity runs opposite to scroll velocity.
    settleSnap({ v: s.v, gain: -1 });
  };
  stage.addEventListener('pointerdown', onStageDown);
  stage.addEventListener('pointermove', onStageMove);
  stage.addEventListener('pointerup', endSwipe);
  stage.addEventListener('pointercancel', endSwipe);

  /* ---------------- zoom ---------------- */
  const zoomBackdrop = el(zoom, 'zoomBackdrop');
  const zoomBox = el(zoom, 'zoomImg');
  const zoomScroll = el(zoom, 'zoomScroll');
  const zoomImgNode = zoomBox.querySelector('img');
  if (!zoomImgNode) throw new Error('PhotoViewer: zoom image missing');
  const zoomImg = zoomImgNode;
  zoomScroll.innerHTML = '';
  const slots = PHOTOS.map((p) => {
    const slot = document.createElement('div');
    slot.className = 'zoom-slot';
    // The frame is the photo's fixed box; magnifying scales the image inside it.
    const frame = document.createElement('div');
    frame.className = 'zoom-frame';
    const img = makeImg(p.src, p.alt, 1600, 1067);
    img.loading = 'lazy';
    frame.appendChild(img);
    slot.appendChild(frame);
    zoomScroll.appendChild(slot);
    return slot;
  });
  const zoomCounter = el(zoom, 'zoomCounter');
  const zoomBar = el(zoom, 'zoomBar');
  const zoomChrome = [zoomCounter, zoomBar];
  let zoomed = -1;
  let zoomAnim: ReturnType<typeof animate> | null = null;
  /** Set while a photo from outside the grid (a floor plan) is zoomed; it closes back to `origin`. */
  let single: { origin: HTMLElement; ratio: number } | null = null;
  let pageOverflow: string | null = null;
  // While a photo is open the page behind must not take wheel or scrollbar input; the zoom
  // stack scrolls instead and shows its own scrollbar in the same place.
  function lockPage() {
    if (pageOverflow !== null) return;
    pageOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
  }
  function unlockPage() {
    if (pageOverflow === null) return;
    document.documentElement.style.overflow = pageOverflow;
    pageOverflow = null;
  }

  function cellRect(idx: number): Rect {
    const b = cells[idx].el.getBoundingClientRect();
    return { x: b.left, y: b.top, w: b.width, h: b.height };
  }
  function fullRect(): Rect {
    // Full width minus the side insets, at the photo's own aspect ratio, starting right under
    // the nav bar. Taller-than-viewport photos are scrolled inside the stack instead of cropped.
    // Phones show zoomed photos edge to edge.
    const inset = isPhone(window.innerWidth) ? 0 : cfg.zoom.sideInset;
    // The stack's own scrollbar (if the browser draws one) takes room from the photo column.
    const scrollbar = zoomScroll.offsetWidth - zoomScroll.clientWidth;
    const availableWidth = window.innerWidth - scrollbar - 2 * inset;
    const availableHeight = slotHeight();
    const ratio = single ? single.ratio : ZOOM_PHOTO_RATIO;
    // Lightbox fit: the whole photo is visible, centred in the space under the bar.
    const w = Math.min(availableWidth, availableHeight * ratio);
    const h = w / ratio;
    return {
      x: (window.innerWidth - scrollbar - w) / 2,
      y: BAR_H + (availableHeight - h) / 2,
      w,
      h,
    };
  }
  /** The space under the bar; each photo in the stack gets exactly one screen of it. */
  function slotHeight() {
    return window.innerHeight - BAR_H;
  }
  function setRect(r: Rect) {
    zoomBox.style.transform = `translate(${r.x.toFixed(2)}px, ${r.y.toFixed(2)}px)`;
    zoomBox.style.width = r.w.toFixed(2) + 'px';
    zoomBox.style.height = r.h.toFixed(2) + 'px';
  }
  function setChrome(o: number) {
    zoomBackdrop.style.opacity = o.toFixed(3);
    for (const node of zoomChrome) node.style.opacity = o.toFixed(3);
  }
  function slotPitch() {
    return slotHeight() + cfg.zoom.stackGap;
  }
  function layoutScroller() {
    // The scroller spans the window so its scrollbar sits at the right edge like the page's,
    // and only covers the visible area under the bar; each slot keeps the photo's height.
    // Size it first so fullRect() can see whether a scrollbar is being drawn.
    zoomScroll.style.transform = `translate(0px, ${BAR_H}px)`;
    zoomScroll.style.height = slotHeight() + 'px';
    const r = fullRect();
    zoom.style.setProperty('--slot-h', slotHeight() + 'px');
    zoom.style.setProperty('--photo-w', r.w + 'px');
    zoom.style.setProperty('--photo-h', r.h + 'px');
    zoom.style.setProperty('--zoom-gap', cfg.zoom.stackGap + 'px');
    zoomScroll.classList.toggle('snap', cfg.zoom.snap);
  }
  function scrollToSlot(idx: number, smooth: boolean) {
    zoomScroll.scrollTo({
      top: idx * slotPitch(),
      behavior: smooth && !reducedMotion.matches ? 'smooth' : 'auto',
    });
  }
  const onZoomScroll = () => {
    if (zoomed < 0 || !zoomScroll.classList.contains('live')) return;
    const idx = Math.max(
      0,
      Math.min(PHOTOS.length - 1, Math.round(zoomScroll.scrollTop / slotPitch())),
    );
    if (idx !== zoomed) {
      unmagnify(true);
      showZoomPhoto(idx);
    }
  };
  zoomScroll.addEventListener('scroll', onZoomScroll, { passive: true });

  /* ---------------- magnify: click a lightbox photo to look closer inside its frame ---------------- */
  // The frame keeps its size; the photo scales up inside it and the visible part follows the
  // mouse (or a finger drag). Clicking again shows the whole photo.
  const MAGNIFY = 2.5;
  const TOGGLE_MS = 260;
  type Magnified = {
    frame: HTMLElement;
    img: HTMLImageElement;
    w: number;
    h: number;
    tx: number;
    ty: number;
  };
  let magnified: Magnified | null = null;
  let touchPan: {
    id: number;
    startX: number;
    startY: number;
    tx: number;
    ty: number;
    moved: boolean;
  } | null = null;
  // A finger drag that panned the photo must not count as the tap that toggles magnification.
  let panMoved = false;
  let toggleTimer: ReturnType<typeof setTimeout> | undefined;
  function frameOf(target: EventTarget | null) {
    const frame = target instanceof Element ? target.closest('.zoom-frame') : null;
    return frame instanceof HTMLElement ? frame : null;
  }
  function clampPan(m: Magnified, tx: number, ty: number) {
    return {
      tx: Math.min(0, Math.max(m.w - m.w * MAGNIFY, tx)),
      ty: Math.min(0, Math.max(m.h - m.h * MAGNIFY, ty)),
    };
  }
  function applyMagnify() {
    if (!magnified) return;
    magnified.img.style.transform = `translate(${magnified.tx.toFixed(2)}px, ${magnified.ty.toFixed(2)}px) scale(${MAGNIFY})`;
  }
  /** Show the part of the photo under the pointer: left edge of the frame ↔ left edge of the photo, etc. */
  function panToPointer(clientX: number, clientY: number) {
    if (!magnified) return;
    const b = magnified.frame.getBoundingClientRect();
    const fx = Math.max(0, Math.min(1, (clientX - b.left) / b.width));
    const fy = Math.max(0, Math.min(1, (clientY - b.top) / b.height));
    const c = clampPan(
      magnified,
      -fx * (magnified.w * MAGNIFY - magnified.w),
      -fy * (magnified.h * MAGNIFY - magnified.h),
    );
    magnified.tx = c.tx;
    magnified.ty = c.ty;
    applyMagnify();
  }
  function animateToggle(frame: HTMLElement) {
    clearTimeout(toggleTimer);
    frame.classList.add('toggling');
    toggleTimer = setTimeout(() => frame.classList.remove('toggling'), TOGGLE_MS);
  }
  function magnifyAt(frame: HTMLElement, clientX: number, clientY: number) {
    const img = frame.querySelector('img');
    if (!img) return;
    const b = frame.getBoundingClientRect();
    magnified = { frame, img, w: b.width, h: b.height, tx: 0, ty: 0 };
    frame.classList.add('magnified');
    animateToggle(frame);
    panToPointer(clientX, clientY);
  }
  function unmagnify(instant = false) {
    if (!magnified) return;
    const { img, frame } = magnified;
    magnified = null;
    touchPan = null;
    frame.classList.remove('magnified', 'panning');
    if (instant) {
      clearTimeout(toggleTimer);
      frame.classList.remove('toggling');
      img.style.transition = 'none';
      img.style.transform = '';
      void img.offsetWidth;
      img.style.transition = '';
    } else {
      animateToggle(frame);
      img.style.transform = '';
    }
  }
  const onMagnifyMove = (e: PointerEvent) => {
    if (!magnified) return;
    if (e.pointerType === 'mouse') {
      if (frameOf(e.target) === magnified.frame) panToPointer(e.clientX, e.clientY);
      return;
    }
    if (!touchPan || e.pointerId !== touchPan.id) return;
    const dx = e.clientX - touchPan.startX;
    const dy = e.clientY - touchPan.startY;
    if (!touchPan.moved) {
      if (Math.hypot(dx, dy) < 4) return;
      touchPan.moved = true;
      magnified.frame.classList.add('panning');
      magnified.img.setPointerCapture(e.pointerId);
    }
    const c = clampPan(magnified, touchPan.tx + dx, touchPan.ty + dy);
    magnified.tx = c.tx;
    magnified.ty = c.ty;
    applyMagnify();
  };
  const onMagnifyDown = (e: PointerEvent) => {
    if (!magnified || e.pointerType === 'mouse' || frameOf(e.target) !== magnified.frame) return;
    touchPan = {
      id: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      tx: magnified.tx,
      ty: magnified.ty,
      moved: false,
    };
  };
  const endTouchPan = (e: PointerEvent) => {
    if (!touchPan || e.pointerId !== touchPan.id) return;
    const moved = touchPan.moved;
    touchPan = null;
    magnified?.frame.classList.remove('panning');
    if (moved) {
      panMoved = true;
      setTimeout(() => {
        panMoved = false;
      }, 0);
    }
  };
  zoomScroll.addEventListener('pointerdown', onMagnifyDown);
  zoomScroll.addEventListener('pointermove', onMagnifyMove);
  zoomScroll.addEventListener('pointerup', endTouchPan);
  zoomScroll.addEventListener('pointercancel', endTouchPan);
  const lerpRect = (a: Rect, b: Rect, t: number): Rect => ({
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
    w: a.w + (b.w - a.w) * t,
    h: a.h + (b.h - a.h) * t,
  });
  function applyZoomStyle() {
    zoom.style.setProperty('--zoom-x', fullRect().x + 'px');
    zoomBackdrop.style.background = cfg.zoom.backdrop;
  }
  function showZoomPhoto(idx: number) {
    zoomed = idx;
    zoomImg.removeAttribute('srcset');
    zoomImg.src = PHOTOS[idx].src;
    zoomImg.alt = PHOTOS[idx].alt;
    zoomCounter.textContent = `${idx + 1} of ${PHOTOS.length}`;
  }
  function showStack(idx: number) {
    layoutScroller();
    zoomScroll.classList.remove('live');
    scrollToSlot(idx, false);
    zoomScroll.classList.add('live');
    zoomBox.style.visibility = 'hidden';
  }
  function hideStack() {
    unmagnify(true);
    zoomScroll.classList.remove('live');
    zoomBox.style.visibility = '';
  }
  function openZoom(idx: number) {
    if (zoomed >= 0 || idx < 0) return;
    zoomAnim?.stop();
    applyZoomStyle();
    showZoomPhoto(idx);
    const from = cellRect(idx);
    hideStack();
    setRect(from);
    setChrome(0);
    zoom.classList.add('on');
    lockPage();
    // Measure the target only once the layer is showing, so its scrollbar is accounted for.
    layoutScroller();
    const to = fullRect();
    if (reducedMotion.matches) {
      setRect(to);
      setChrome(1);
      showStack(idx);
      return;
    }
    zoomAnim = animate(0, 1, {
      ...cfg.zoom.open,
      onUpdate: (p: number) => {
        setRect(lerpRect(from, to, p));
        setChrome(p);
      },
    });
    void zoomAnim.then(() => {
      if (zoomed === idx) showStack(idx);
    });
  }
  function closeZoom(immediate = false) {
    if (zoomed < 0) return;
    zoomAnim?.stop();
    unmagnify(true);
    const idx = zoomed;
    if (!single) {
      const [a, b] = rangeAt(stage.scrollLeft);
      if (idx < a || idx > b) scrollToPhoto(idx, false);
    }
    let from: Rect;
    if (!single && zoomScroll.classList.contains('live')) {
      showZoomPhoto(idx);
      const sb = (slots[idx].firstElementChild ?? slots[idx]).getBoundingClientRect();
      from = { x: sb.left, y: sb.top, w: sb.width, h: sb.height };
      setRect(from);
      hideStack();
    } else {
      from = {
        x: 0,
        y: 0,
        w: parseFloat(zoomBox.style.width),
        h: parseFloat(zoomBox.style.height),
      };
      const m = zoomBox.style.transform.match(/translate\(([-\d.]+)px, ([-\d.]+)px\)/);
      if (m) {
        from.x = +m[1];
        from.y = +m[2];
      }
    }
    // Give the page its scrollbar back before measuring where the photo lands, so the target
    // cell is where it will be once the zoom layer is gone.
    unlockPage();
    const to = single ? rectOf(single.origin) : cellRect(idx);
    const finishClose = () => {
      zoom.classList.remove('on');
      zoom.classList.remove('single');
      single = null;
      zoomed = -1;
    };
    if (reducedMotion.matches || immediate) {
      finishClose();
      return;
    }
    zoomAnim = animate(1, 0, {
      ...cfg.zoom.close,
      onUpdate: (p: number) => {
        setRect(lerpRect(to, from, p));
        setChrome(p);
      },
    });
    void zoomAnim.then(() => {
      finishClose();
    });
  }
  function rectOf(node: Element): Rect {
    const b = node.getBoundingClientRect();
    return { x: b.left, y: b.top, w: b.width, h: b.height };
  }
  /** Zoom a photo that is not part of the grid (a floor plan): no counter, no photo stack. */
  function openSingle(photo: { src: string; alt: string }, origin: HTMLElement) {
    if (zoomed >= 0) return;
    zoomAnim?.stop();
    const node = origin.querySelector('img') ?? origin;
    const from = rectOf(node);
    const ratio =
      node instanceof HTMLImageElement && node.naturalWidth > 0 && node.naturalHeight > 0
        ? node.naturalWidth / node.naturalHeight
        : from.w / Math.max(1, from.h);
    single = { origin: node, ratio };
    // Past the last grid photo: "a photo is open", without pointing at a cell.
    zoomed = PHOTOS.length;
    zoomImg.src = photo.src;
    zoomImg.alt = photo.alt;
    zoom.classList.add('single');
    applyZoomStyle();
    hideStack();
    setRect(from);
    setChrome(0);
    zoom.classList.add('on');
    lockPage();
    const to = fullRect();
    if (reducedMotion.matches) {
      setRect(to);
      setChrome(1);
      return;
    }
    zoomAnim = animate(0, 1, {
      ...cfg.zoom.open,
      onUpdate: (p: number) => {
        setRect(lerpRect(from, to, p));
        setChrome(p);
      },
    });
  }
  function zoomStep(dir: number) {
    if (single) return;
    const n = zoomed + dir;
    if (n < 0 || n >= PHOTOS.length) return;
    showZoomPhoto(n);
    if (zoomScroll.classList.contains('live')) scrollToSlot(n, true);
  }
  const onSlotClick = (e: MouseEvent) => {
    if (!(e.target instanceof Element) || !e.target.closest('.zoom-slot')) return;
    if (panMoved) return;
    const frame = frameOf(e.target);
    if (frame) {
      // On the photo: toggle a closer look. On the dark area around it: close.
      if (magnified) unmagnify();
      else magnifyAt(frame, e.clientX, e.clientY);
      return;
    }
    closeZoom();
  };
  zoomScroll.addEventListener('click', onSlotClick);

  const WHEEL_IDLE_MS = 120;
  let wheel: { v: number; lastT: number } | null = null;
  let wheelT: ReturnType<typeof setTimeout> | undefined;
  const onWheel = (e: WheelEvent) => {
    if (zoomed >= 0) return;
    const horizontal = Math.abs(e.deltaX) > Math.abs(e.deltaY) || e.shiftKey;
    if (!horizontal) return;
    e.preventDefault();
    let d = e.shiftKey && Math.abs(e.deltaY) > Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
    if (e.deltaMode === 1) d *= 16;
    else if (e.deltaMode === 2) d *= stage.clientWidth;
    const now = performance.now();
    if (!wheel) {
      if (drag) return;
      stopAnim();
      navTarget = null;
      beginLive();
      wheel = { v: 0, lastT: now };
    }
    const dt = Math.max(1, now - wheel.lastT);
    wheel.v = 0.75 * wheel.v + 0.25 * (d / dt);
    wheel.lastT = now;
    stage.scrollLeft = Math.max(0, Math.min(maxScroll(), stage.scrollLeft + d));
    syncScrubber();
    clearTimeout(wheelT);
    wheelT = setTimeout(() => {
      const w = wheel;
      wheel = null;
      if (w) settleSnap({ v: w.v, gain: 1 });
    }, WHEEL_IDLE_MS);
  };
  stage.addEventListener('wheel', onWheel, { passive: false });
  const cellIndexOf = (target: EventTarget | null) => {
    const cell = target instanceof Element ? target.closest('.cell') : null;
    return cell ? cells.findIndex((c) => c.el === cell) : -1;
  };
  const onCellClick = (e: MouseEvent) => {
    if (suppressCellClick) return;
    const idx = cellIndexOf(e.target);
    if (idx >= 0) openZoom(idx);
  };
  track.addEventListener('click', onCellClick);
  // A click listener receives the event. closeZoom treats a truthy argument as
  // "skip the animation", so the event must not be forwarded.
  const closeZoomFromClick = () => {
    closeZoom();
  };
  zoomBackdrop.addEventListener('click', closeZoomFromClick);
  zoomBox.addEventListener('click', closeZoomFromClick);
  const PINCH_MIN = 2;
  const onPinch = (e: WheelEvent) => {
    if (!e.ctrlKey) return;
    if (zoomed >= 0) {
      e.preventDefault();
      if (e.deltaY > PINCH_MIN) closeZoom();
    } else if (e.deltaY < -PINCH_MIN) {
      const idx = cellIndexOf(e.target);
      if (idx < 0) return;
      e.preventDefault();
      openZoom(idx);
    }
  };
  zoom.addEventListener('wheel', onPinch, { passive: false });
  stage.addEventListener('wheel', onPinch, { passive: false });
  const onWinResize = () => {
    if (zoomed < 0) return;
    zoomAnim?.stop();
    applyZoomStyle();
    setRect(fullRect());
    setChrome(1);
    if (zoomScroll.classList.contains('live')) {
      layoutScroller();
      scrollToSlot(zoomed, false);
    }
  };
  window.addEventListener('resize', onWinResize);

  const onLike = () => {
    const liked = likeBtn.classList.toggle('liked');
    callbacks.onLike?.(liked);
  };
  const onShare = () => callbacks.onShare?.();
  likeBtn.addEventListener('click', onLike);
  shareBtn.addEventListener('click', onShare);

  const onResize = () => {
    if (PHOTOS.length === 0) return;
    const anchor = visibleRange()[0];
    stopAnim();
    stopBlend();
    liveMode = false;
    fromAv = null;
    fromRange = null;
    sizeColumns();
    sizeThumbs();
    scrollToPhoto(anchor, false);
  };
  function applyFrame() {
    const g = geometry(root.clientWidth, cfg.grid);
    root.style.height = g.frameH + 'px';
    root.style.setProperty('--pad', cfg.grid.padding + 'px');
    root.style.setProperty('--gap', cfg.grid.gap + 'px');
    root.style.setProperty('--stage-radius', cfg.grid.topRadius + 'px');
    if (heroOverlay instanceof HTMLElement) {
      heroOverlay.style.left = '0px';
      heroOverlay.style.top = '0px';
      heroOverlay.style.width = g.heroW + 'px';
      heroOverlay.style.height = g.heroH + 'px';
    }
  }
  applyFrame();
  applyZoomStyle();
  let lastW = root.clientWidth;
  const ro = new ResizeObserver(() => {
    if (root.clientWidth === lastW) return;
    lastW = root.clientWidth;
    applyFrame();
    onResize();
  });

  pill.setAttribute('role', 'slider');
  pill.setAttribute('tabindex', '0');
  pill.setAttribute('aria-label', 'Photo scrubber');
  pill.setAttribute('aria-valuemin', '1');
  pill.setAttribute('aria-valuemax', String(PHOTOS.length));

  buildStage();
  buildThumbs();
  // The grid now shows the hero itself.
  const ssrHero = root.querySelector('.ssr-hero');
  if (ssrHero instanceof HTMLElement) ssrHero.hidden = true;
  rTo = rangeAt(0);
  blend = 1;
  syncScrubber();
  ro.observe(root);

  return {
    closeZoom,
    openPhoto: openSingle,
    destroy: () => {
      unlockPage();
      ro.disconnect();
      stopAnim();
      stopBlend();
      clearTimeout(wheelT);
      zoomAnim?.stop();
      document.body.style.cursor = '';
      if (!inert) window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onWinResize);
      zoom.classList.remove('on');
    },
  };
}

const ZOOM_BUTTON_BASE =
  'inline-flex items-center justify-center whitespace-nowrap rounded-xl px-5 py-1.5 text-[14px] leading-5 font-medium';
const ZOOM_BUTTON_PRIMARY =
  'bg-[#0040E6] text-white shadow-[0_2px_3px_0_rgba(0,64,230,0.15),inset_0_2px_2px_0_#0266F2]';
const ZOOM_BUTTON_SECONDARY =
  'bg-white text-[#231E1B] shadow-[0_2px_3px_0_rgba(137,134,127,0.15),inset_0_0_0_1px_#D7D4CD]';

type TourAction = NonNullable<ReturnType<typeof useTouring>>['primaryAction'];

const ZoomBarButton = ({
  action,
  label = action.text,
  primary,
}: {
  action: TourAction;
  label?: string;
  primary: boolean;
}) => {
  const className = `${ZOOM_BUTTON_BASE} ${primary ? ZOOM_BUTTON_PRIMARY : ZOOM_BUTTON_SECONDARY}`;
  if (action.href && !action.disabled) {
    return (
      <a
        href={action.href}
        target={action.target}
        rel="noopener"
        aria-label={label}
        onClick={action.onClick}
        className={className}
      >
        {label}
      </a>
    );
  }
  return (
    <button
      type="button"
      aria-label={label}
      disabled={action.disabled}
      onClick={action.onClick}
      className={className}
    >
      {label}
    </button>
  );
};

/** The self-tour / buy buttons for the zoom bar, mirroring the price card's choices. */
const ZoomBarActions = ({
  property,
  onDialogActive,
}: {
  property: PropertyDetailsType;
  onDialogActive: (active: boolean) => void;
}) => {
  const touring = useTouring(property, { placement: 'lightbox' });
  const dialogActive = Boolean(
    touring &&
    (touring.isDialogOpen || touring.isSelfTourDialogOpen || touring.isBuyDirectContextOpen),
  );
  useEffect(() => {
    onDialogActive(dialogActive);
    return () => onDialogActive(false);
  }, [dialogActive, onDialogActive]);
  if (!touring) return null;
  const tourIsSelf = touring.tourKind === 'selfTour';
  const leftIsPrimary =
    touring.tourKind === 'selfTour' || touring.secondaryAction?.variant === 'primary';
  const rightIsPrimary = !touring.showBuyDirect && touring.primaryAction.variant !== 'secondary';
  return (
    <>
      <div className="zoom-bar-actions flex items-center gap-3">
        {touring.secondaryAction && (
          <ZoomBarButton
            action={touring.secondaryAction}
            label={tourIsSelf ? 'Take a self tour' : undefined}
            primary={leftIsPrimary}
          />
        )}
        <ZoomBarButton
          action={touring.primaryAction}
          label={!touring.showBuyDirect && tourIsSelf ? 'Take a self tour' : undefined}
          primary={rightIsPrimary}
        />
      </div>
      {touring.tourKind === 'tourWithAgent' && (
        <TourWithAgentModal
          property={property}
          isOpen={touring.isDialogOpen}
          onClose={touring.closeDialog}
        />
      )}
      <SelfTourDialog
        isOpen={touring.isSelfTourDialogOpen}
        onClose={touring.closeSelfTourDialog}
        onOpenRequest={touring.openSelfTourDialog}
        placement="lightbox"
        addressToken={property.addressToken}
        slug={property.slug}
        tourBookingListingId={property.tourBookingListingId}
        addressLine={[property.street, property.city].filter(Boolean).join(', ')}
      />
      <BuyDirectContextModal
        isOpen={touring.isBuyDirectContextOpen}
        onClose={touring.closeBuyDirectContext}
        continueHref={touring.buyDirectContextHref}
      />
    </>
  );
};

const VIEWER_SRCSET_PRESETS = [
  ['3:2-sm', 768],
  ['3:2-md', 1200],
  ['3:2-lg', 1440],
  ['3:2-xl', 1800],
] as const;

const viewerPhotoSrcSet = (photo: GalleryPhoto): string | undefined => {
  const source = toImagesWorkerSourceUrl(photo.url);
  if (!source) return undefined;
  return VIEWER_SRCSET_PRESETS.map(
    ([preset, width]) =>
      `${buildWorkerPhotoUrl(source, { preset, presetScale: '1', dpr: '1' })} ${width}w`,
  ).join(', ');
};

const viewerPhotoSrc = (photo: GalleryPhoto): string => {
  const source = toImagesWorkerSourceUrl(photo.url);
  if (source) return buildWorkerPhotoUrl(source, { preset: '3:2-xl', presetScale: '1', dpr: '1' });
  return pickFirstPresizedUrl(photo, ['xl', 'lg', 'md']) ?? photo.url;
};

/** Listing photos only: floor plans keep their own section and open in the lightbox. */
const buildViewerPhotos = (property: PropertyDetailsType): ViewerPhoto[] =>
  buildGalleryPhotos(property)
    .filter((photo) => !photo.isFloorPlan)
    .map((photo, index) => ({
      // One ~1800px 3:2 render shared by the cell, the zoom morph and the zoom stack.
      src: viewerPhotoSrc(photo),
      srcSet: viewerPhotoSrcSet(photo),
      thumb: getLightboxThumbnailUrl(photo),
      alt: `Photo ${index + 1} of ${property.street ?? 'this home'}`,
      // Hero gets its own column; after it, every third photo is a full-height single.
      wide: index === 0 || index % 3 === 0,
    }));

type PdpPhotoViewerProps = {
  property: PropertyDetailsType;
  onShare: () => void;
  heroOverlay?: ReactNode;
};

/** What the page can ask the viewer to do from outside, e.g. zoom a floor plan. */
export type PdpPhotoViewerHandle = {
  openPhoto: (photo: { src: string; alt: string }, origin: HTMLElement) => void;
};

export const PdpPhotoViewer = forwardRef<PdpPhotoViewerHandle, PdpPhotoViewerProps>(
  function PdpPhotoViewer({ property, onShare, heroOverlay }, ref) {
    const rootRef = useRef<HTMLDivElement>(null);
    const zoomRef = useRef<HTMLDivElement>(null);
    const onZoomDialogActive = useCallback((active: boolean) => {
      zoomRef.current?.classList.toggle('yield', active);
    }, []);
    const likeRef = useRef<HTMLButtonElement>(null);
    const controlsRef = useRef<ReturnType<typeof mountViewer> | null>(null);
    // The zoom layer portals into document.body, so it only renders once we are on the client.
    const mounted = useSyncExternalStore(
      () => () => {},
      () => true,
      () => false,
    );
    // The viewer engine mounts once; it reads the latest handlers through this ref.
    const callbacks = useRef({ onShare, onLike: property.onToggleFavorite });
    useEffect(() => {
      callbacks.current = { onShare, onLike: property.onToggleFavorite };
    });
    // Built once per mounted viewer (the parent remounts it per home via `key`).
    const [photos] = useState(() => buildViewerPhotos(property));

    useEffect(() => {
      const root = rootRef.current;
      const zoom = zoomRef.current;
      if (!mounted || !root || !zoom) return undefined;
      const controls = mountViewer(root, zoom, photos, CONFIG, {
        onShare: () => callbacks.current.onShare(),
        onLike: () => callbacks.current.onLike(),
      });
      controlsRef.current = controls;
      return () => {
        controlsRef.current = null;
        controls.destroy();
      };
    }, [mounted, photos]);
    // The heart reflects the real favorite state, not just the last click.
    useEffect(() => {
      likeRef.current?.classList.toggle('liked', property.isLiked);
    }, [property.isLiked]);
    useImperativeHandle(
      ref,
      () => ({ openPhoto: (photo, origin) => controlsRef.current?.openPhoto(photo, origin) }),
      [],
    );

    const address = formatStreetAddress(property.street, property.unitType, property.unitNumber);
    const locality = [[property.city, property.state].filter(Boolean).join(', '), property.zip]
      .filter(Boolean)
      .join(' ');
    const emptyLocation =
      photos.length === 0
        ? streetViewLocationFor({
            availableSoon: property.listingState === 'AVAILABLE_SOON',
            photoCount: 0,
            latitude: property.latitude,
            longitude: property.longitude,
          })
        : null;

    const zoomLayer = (
      <div ref={zoomRef} className="pv-zoom" aria-modal="true" role="dialog">
        <div className="zoom-backdrop" data-pv="zoomBackdrop" />
        <div className="zoom-bar" data-pv="zoomBar">
          <div className="zoom-bar-address">
            <span className="text-[14px] leading-5 text-[#231E1B]">{address}</span>
            <span className="text-[14px] leading-5 text-[#5F5F5F]">{locality}</span>
          </div>
          <div className="zoom-bar-right">
            <ZoomBarActions property={property} onDialogActive={onZoomDialogActive} />
            <div className="zoom-bar-icons">
              <button
                type="button"
                className="icon-btn"
                aria-label="Share home"
                onClick={() => {
                  controlsRef.current?.closeZoom(true);
                  callbacks.current.onShare();
                }}
              >
                <IconShare2 size={24} stroke={2} />
              </button>
              <button
                type="button"
                className={property.isLiked ? 'icon-btn liked' : 'icon-btn'}
                aria-label={property.isLiked ? 'unfavorite' : 'favorite'}
                aria-pressed={property.isLiked}
                onClick={() => callbacks.current.onLike()}
              >
                <IconHeart size={24} stroke={2} />
              </button>
              <button
                type="button"
                className="icon-btn"
                aria-label="Close photos"
                onClick={() => controlsRef.current?.closeZoom()}
              >
                <IconX size={24} stroke={2} />
              </button>
            </div>
          </div>
        </div>
        <div className="zoom-img" data-pv="zoomImg">
          <img alt="" draggable="false" />
        </div>
        <div className="zoom-scroll" data-pv="zoomScroll" />
        <div className="counter" data-pv="zoomCounter">
          1 of {photos.length}
        </div>
      </div>
    );

    let stageHero: ReactNode;
    if (photos.length > 0) {
      const hero = photos[0];
      stageHero = (
        <img
          className="ssr-hero"
          src={hero.src}
          srcSet={hero.srcSet}
          sizes={hero.srcSet ? VIEWER_IMAGE_SIZES : undefined}
          alt={hero.alt}
          fetchPriority="high"
          decoding="async"
          draggable="false"
        />
      );
    } else if (emptyLocation) {
      stageHero = (
        <div className="empty-hero">
          <StreetViewPhoto
            latitude={emptyLocation.latitude}
            longitude={emptyLocation.longitude}
            alt={streetViewAltText(property)}
            fallbackLabel="Photos coming soon"
          />
        </div>
      );
    } else {
      stageHero = (
        <div className="empty-hero">
          <PropertyPhotoPlaceholder
            state="unavailable"
            label={property.listingState === 'AVAILABLE_SOON' ? 'Photos coming soon' : undefined}
          />
        </div>
      );
    }

    return (
      <div ref={rootRef} className={photos.length === 0 ? 'pv-root empty' : 'pv-root'}>
        {/* Inserted as raw text so server and browser produce the identical style node. */}
        <style dangerouslySetInnerHTML={{ __html: CSS }} />
        {mounted && createPortal(zoomLayer, document.body)}
        <div className="viewer" data-pv="viewer">
          <div className="counter" data-pv="counter">
            1 of {photos.length}
          </div>
          <div className="actions">
            <button
              type="button"
              className="icon-btn"
              data-pv="share"
              title="Share"
              aria-label="Share home"
            >
              <IconShare2 size={24} stroke={2} />
            </button>
            <button
              ref={likeRef}
              type="button"
              className={property.isLiked ? 'icon-btn liked' : 'icon-btn'}
              data-pv="like"
              title="Favorite"
              aria-label={property.isLiked ? 'unfavorite' : 'favorite'}
              aria-pressed={property.isLiked}
            >
              <IconHeart size={24} stroke={2} />
            </button>
          </div>
          <div className="stage" data-pv="stage">
            {/* Visible before hydration (and without JavaScript); the engine hides it once the grid is built. */}
            {stageHero}
            <div className="track" data-pv="track" />
            {heroOverlay && (
              <div className="hero-overlay" data-pv="heroOverlay">
                {heroOverlay}
              </div>
            )}
          </div>
          <div className="scrubber" data-pv="scrubber">
            <div className="strip-wrap">
              <div className="pill" data-pv="pill">
                <div className="clip" data-pv="clip">
                  <div className="rail" data-pv="rail">
                    <div className="window" data-pv="window" />
                    <div className="thumbs" data-pv="thumbs" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  },
);
